import { useMemo } from 'react';
import type { UseSheetData } from '@/hooks/useSheetData';
import { useMeterList } from '@/hooks/useMeterList';
import { MeterPicker } from '@/components/dashboard/MeterPicker';
import { KpiStrip } from '@/components/dashboard/KpiStrip';
import { PowerAnalysis } from '@/components/dashboard/PowerAnalysis';
import { DeepDive } from '@/components/dashboard/DeepDive';
import { PageLoadingSkeleton, PageErrorState, StaleDataBanner, PageHeader } from '@/components/ui/PageState';
import { buildKpiSummary, powerFactor, maxDemandKvaOf } from '@/lib/calculations';
import { dailyKvahKwhWB, mtdKvahKwhWB, powerFactorFromPair } from '@/lib/west-bengal/pf';
import { buildBillBreakdown } from '@/lib/billing';
import { useTariff } from '@/contexts/TenantContext';
import { useAuth } from '@/auth/AuthContext';
import { Cpu } from 'lucide-react';

interface DashboardProps { data: UseSheetData }

export function Dashboard({ data }: DashboardProps) {
  const { rows, status, error } = data;
  const { meters, selectedMeter, setSelectedMeter, meterRows } = useMeterList(rows);
  const tariff    = useTariff();
  const { selectedSheet } = useAuth();

  // ── Base KPI from the generic calculation engine ──────────────────────────
  // Provides pfNow, pfYesterday, pfMtd, riskNow, freshSeconds, and kVAh
  // apparent-energy fields. Cost fields are ALWAYS overridden below so both
  // Dashboard and Billing pages share one source of truth.
  const kpiBase = useMemo(() => buildKpiSummary(meterRows), [meterRows]);

  // ── Single source of truth: replicate Billings.tsx cost formula exactly ───
  //
  // Root cause of the mismatch:
  //   OLD Dashboard (WBSEDCL path) returned kpiBase unchanged → costs came from
  //   costRupees(), a per-row effectiveRate integration that used only the flat
  //   NORMAL rate (pfRebatePct = 0 in derivedEngine) with no ToD split.
  //   Billings.tsx used piTotalCostMtd (derivedEngine accumulator) when non-zero,
  //   otherwise the breakdown fallback — producing a different value on the hero.
  //
  // Fix: for WBSEDCL, mirror Billings.tsx totalCostMtd logic exactly:
  //   piTotalCostMtd = lastMtdRow?.totalCost  (derivedEngine running accumulator)
  //   fallback       = max(0, breakdown.ecTotalRs + breakdown.pfAdjTotalRs + breakdown.lfRebateRs)
  //
  // For today/yesterday cost, Billings.tsx uses:
  //   WB: finite(lastDayRow?.dailyNetEC ?? latestRow?.dailyCostRs)
  //        || todayNetEnergyCostRs (= max(0, ec+pfAdj+lfRebate) from todayBreakdown)
  //   Both paths are replicated below.
  //
  // TGSPDCL: unchanged — still uses breakdown.ecTotalRs (energy-only, demand excluded).
  const contractDemandKva = selectedSheet?.meta?.contractDemandKva ?? tariff.defaultContractDemandKva;

  const kpi = useMemo(() => {
    const isWb = tariff.energyUnit !== 'kvah'; // WBSEDCL uses kWh; TGSPDCL uses kVAh

    // Replicate the exact date-windowing from Billings.tsx so row sets match.
    const latest = meterRows.length ? meterRows[meterRows.length - 1] : null;
    if (!latest) return kpiBase;

    const dataEnd   = latest.timestamp;
    const todayDate = latest.date; // YYYY-MM-DD local-time (matches Billings.tsx)

    // Yesterday date string — same construction as Billings.tsx / buildKpiSummary
    const y = dataEnd.getFullYear(), mo = dataEnd.getMonth(), d = dataEnd.getDate();
    const yd = new Date(y, mo, d - 1);
    const yesterdayDate = `${yd.getFullYear()}-${String(yd.getMonth() + 1).padStart(2, '0')}-${String(yd.getDate()).padStart(2, '0')}`;

    // Month-to-date window — mirrors Billings.tsx billingRows filter
    const monthStartTs = new Date(y, mo, 1, 0, 0, 0, 0).getTime();

    const todayRows     = meterRows.filter(r => r.date === todayDate);
    const yesterdayRows = meterRows.filter(r => r.date === yesterdayDate);
    const mtdRows       = meterRows.filter(r => r.timestamp.getTime() >= monthStartTs);

    // ── WB-ONLY: date list for dailyKvahKwhWB / mtdKvahKwhWB ─────────────────
    // dailyKvahKwhWB looks up its own bridge/anchor row from `meterRows`
    // internally (mirroring HourlyLogPdfCardWB's dailyHourSlots bridgeRow),
    // so the Dashboard cards use the EXACT SAME per-day calculation as the
    // PDF — guaranteeing they show the same kWh/kVAh/PF for the same date.
    // Telangana is unaffected — these are only consumed in the isWb branch.
    const mtdDates = [...new Set(mtdRows.map(r => r.date))].sort();

    // buildBillBreakdown — same function + same args as Billings.tsx
    const todayBd = todayRows.length     >= 2 ? buildBillBreakdown(todayRows,     undefined, undefined, contractDemandKva, tariff) : null;
    const yesterBd = yesterdayRows.length >= 2 ? buildBillBreakdown(yesterdayRows, undefined, undefined, contractDemandKva, tariff) : null;
    const mtdBd   = mtdRows.length       >= 2 ? buildBillBreakdown(mtdRows,       undefined, undefined, contractDemandKva, tariff, selectedSheet?.meta?.acdSurchargeRs ?? 0) : null;

    // ── MTD cost — exact mirror of Billings.tsx totalCostMtd ──────────────
    // Billings.tsx line 516-518:
    //   const totalCostMtd = tariff.energyUnit === 'kvah'
    //     ? breakdown.ecTotalRs          // TGSPDCL: energy-only
    //     : (piTotalCostMtd || (breakdown.ecTotalRs + breakdown.pfAdjTotalRs + breakdown.lfRebateRs));
    //
    // piTotalCostMtd = latest?.totalCost (derivedEngine running accumulator).
    // Here we use the last MTD row's .totalCost (same field, same row in practice
    // since Billings.tsx also uses rows[rows.length-1] as 'latest').
    // Full MTD bill — mirrors Billings.tsx hero card exactly.
    // WB: replicate the unrounded demand path from Billings.tsx so the value matches.
    //   mtdBd.grossPayableRs uses billing.ts's internal dcRs (rounded maxDemandKvaOf),
    //   but Billings.tsx overrides demand with the unrounded float — we must do the same.
    // TGSPDCL: grossPayableRs is correct (demand rounding is not overridden there).
    const costMtd = (() => {
      if (!mtdBd) return 0;
      if (!isWb) return mtdBd.grossPayableRs;  // TGSPDCL: use breakdown directly
      // WB: rebuild gross using same unrounded demand formula as Billings.tsx
      const CD          = contractDemandKva;
      const minCD       = CD * tariff.minDemandPct;
      const peakKva     = maxDemandKvaOf(mtdRows);
      const normalDc    = Math.max(peakKva, minCD) * tariff.demandChargeRsKva;
      const addlDc      = Math.max(0, peakKva - CD) * tariff.demandPenalRsKva;
      const totalDc     = normalDc + addlDc;
      // Net energy (after PF + LF adjustments) — from breakdown
      const netEc       = mtdBd.ecTotalRs + mtdBd.pfAdjTotalRs + mtdBd.lfRebateRs;
      const subTotal    = Math.max(0, netEc + totalDc);
      const ed          = subTotal * tariff.electricityDutyValue;      // 12.5% of bill
      const gross       = subTotal + ed + tariff.meterRentalRsMonth;
      return gross;  // gross payable (no TPR — same as Billings hero card)
    })();

    // ── Today cost — exact mirror of Billings.tsx dailyCostRs (WB path) ───
    // Billings.tsx line 570-574:
    //   WB: finite(lastTodayRow?.dailyNetEC ?? latest?.dailyCostRs)
    //        || todayNetEnergyCostRs
    //        || max(0, dailyEnergyCostRs - pfRebate + pfPenalty - lfRebate)
    // todayNetEnergyCostRs = max(0, todayBreakdown.ecTotalRs + pfAdjTotalRs + lfRebateRs)
    const lastTodayRow = todayRows.length ? todayRows[todayRows.length - 1] : latest;
    const todayNetEnergyCostRs = todayBd
      ? Math.max(0, todayBd.ecTotalRs + todayBd.pfAdjTotalRs + todayBd.lfRebateRs) : 0;
    const costToday = tariff.energyUnit === 'kvah'
      ? (todayNetEnergyCostRs || (todayBd?.ecTotalRs ?? 0))   // TGSPDCL: energy-only
      : (((lastTodayRow?.dailyNetEC ?? 0) || (lastTodayRow?.dailyCostRs ?? 0))  // WB: Pi daily acc
          || todayNetEnergyCostRs);                                               // WB: fallback

    // ── Yesterday cost — same pattern as today (using yesterday's last row) ─
    const lastYestRow = yesterdayRows.length ? yesterdayRows[yesterdayRows.length - 1] : null;
    const yesterNetEnergyCostRs = yesterBd
      ? Math.max(0, yesterBd.ecTotalRs + yesterBd.pfAdjTotalRs + yesterBd.lfRebateRs) : 0;
    const costYesterday = tariff.energyUnit === 'kvah'
      ? (yesterNetEnergyCostRs || (yesterBd?.ecTotalRs ?? 0))  // TGSPDCL: energy-only
      : (((lastYestRow?.dailyNetEC ?? 0) || (lastYestRow?.dailyCostRs ?? 0))
          || yesterNetEnergyCostRs);

    // periods[].kwh = energyDelivered (kWh active) — same source as Billings kWh display
    const sumKwh = (bd: ReturnType<typeof buildBillBreakdown> | null) =>
      bd?.periods.reduce((s, p) => s + p.kwh, 0) ?? 0;

    // ── WB-ONLY: per-day kWh/kVAh via the SAME slot methodology as the PDF ──
    // dailyKvahKwhWB / mtdKvahKwhWB (lib/west-bengal/pf.ts) reproduce
    // HourlyLogPdfCardWB's dailyHourSlots() day-total exactly, so these
    // numbers match "kWh Today" / "kVAh Today" in the downloaded PDF for the
    // same date. PF is then derived from THAT SAME (kwh, kvah) pair, so
    // kVAh ≥ kWh and 0 ≤ PF ≤ 1 always hold and pf*/kvah* can never disagree.
    const wbToday     = isWb ? dailyKvahKwhWB(meterRows, todayDate)     : null;
    const wbYesterday = isWb ? dailyKvahKwhWB(meterRows, yesterdayDate) : null;
    const wbMtd       = isWb ? mtdKvahKwhWB(meterRows, mtdDates)        : null;

    return {
      ...kpiBase,
      // ── Cost: now identical to Billing page for both WBSEDCL and TGSPDCL ─
      costToday,
      costYesterday,
      costMtd,
      // ── kVAh: totalKvah from breakdown (TGSPDCL) or WB per-day slot total ──
      // WBSEDCL: breakdown.totalKvah = 0 (kWh tariff). previously used
      // kpiBase.kvah* (apparentEnergyKvah) / apparentEnergyKvahWB, both of
      // which could disagree with the PDF and with pf* for the same window
      // (see lib/west-bengal/pf.ts). wbToday/wbYesterday/wbMtd are computed
      // via the SAME per-hour-slot method as the PDF, so this now matches
      // "kVAh Today" in the downloaded log exactly.
      kvahToday:     isWb ? wbToday!.kvah     : (todayBd?.totalKvah  ?? 0),
      kvahYesterday: isWb ? wbYesterday!.kvah : (yesterBd?.totalKvah ?? 0),
      kvahMtd:       isWb ? wbMtd!.kvah       : (mtdBd?.totalKvah    ?? 0),
      // ── kWh: breakdown per-period sum (TGSPDCL) or WB per-day slot total ──
      // WB kWh now comes from the SAME (kwh, kvah) pair as kvah* above, so
      // PF = kWh/kVAh derived below is always internally consistent.
      kwhToday:      isWb ? wbToday!.kwh     : (sumKwh(todayBd)  || kpiBase.kwhToday),
      kwhYesterday:  isWb ? wbYesterday!.kwh : (sumKwh(yesterBd) || kpiBase.kwhYesterday),
      kwhMtd:        isWb ? wbMtd!.kwh       : (sumKwh(mtdBd)    || kpiBase.kwhMtd),
      // ── Power Factor (WB) ───────────────────────────────────────────────
      // previously kpiBase.pf* (registerPF) or powerFactorWB on a
      // differently-anchored window than kvah* — could disagree with kvah*
      // and even exceed 1. powerFactorFromPair derives PF = kWh/kVAh from
      // THE SAME (wbToday/wbYesterday/wbMtd) pair used for kvah*/kwh* above,
      // always in [0, 1] and always consistent with the displayed kVAh.
      // pfNow stays the live instantaneous reading for both states.
      pfNow:         powerFactor(latest),
      pfYesterday:   isWb ? powerFactorFromPair(wbYesterday!.kwh, wbYesterday!.kvah) : kpiBase.pfYesterday,
      pfMtd:         isWb ? powerFactorFromPair(wbMtd!.kwh,       wbMtd!.kvah)       : kpiBase.pfMtd,
    };
  }, [meterRows, tariff, contractDemandKva, kpiBase]);

  if (status === 'loading' && !rows.length) return <PageLoadingSkeleton />;
  if (status === 'error'   && !rows.length) return <PageErrorState message={error} onRetry={() => void data.refetch()} />;

  return (
    <div className="space-y-5 pt-3 pb-2 animate-fade-in">
      <PageHeader
        title="Power Analysis"
      />

      {status === 'error' && rows.length ? <StaleDataBanner /> : null}

      {/* Meter overview */}
      <section className="space-y-2">
        <div className="flex items-end justify-between px-1">
          <h2 className="font-display text-base font-semibold text-ink-900">Meter overview</h2>
          <span className="flex items-center gap-1 text-[11px] text-ink-500">
            <Cpu className="w-3.5 h-3.5" />
            {meters.length} meter{meters.length === 1 ? '' : 's'}
          </span>
        </div>
        {meters.length ? (
          <MeterPicker meters={meters} value={selectedMeter} onChange={setSelectedMeter} />
        ) : null}
      </section>

      <KpiStrip summary={kpi} />
      <PowerAnalysis meterRows={meterRows} />
      <DeepDive meterRows={meterRows} />
    </div>
  );
}